import PDF from './pdf'
import { formatAccounting, capitalizeFirstLetter, getCurrentDateEnUsFormat } from '../lib/utils'

export default class LoanSummary extends PDF {
  /**
   * Description
   * @param {import('jspdf').DocumentProperties} args
   */
  constructor (args = {}) {
    super()
    this.doc.setProperties({
      unit: 'mm',
      format: 'a4',
      ...args,
    })
    this.addFonts()
  }

  /**
   * Description
   * @param {boolean} [cancel]
   */
  __header (cancel) {
    const title = !cancel ? `${capitalizeFirstLetter(this.getStoreName())} Report` : `Cancelled ${capitalizeFirstLetter(this.getStoreName())} Report`
    this.generateText({ fontSize: 24, text: title, coordinates: [65, 15], color: [55, 71, 79] })
    this.generateText({ fontSize: 12, text: getCurrentDateEnUsFormat(), coordinates: [100, 22] })
    this.doc.setFillColor('#CFD8DC')
    this.doc.line(15, 25, 195, 25, 'F')
  }

  /**
   * Description
   * @param {SummaryData} subHeader
   * @returns {void}
   */
  __subHeader (subHeader) {
    const { name = '', totalAmount, paidAmount = '', balance = '', receiptNo = 'xxxxxxxx' } = subHeader || {}
    const [x, y] = [15, 40]
    const delimiter = ':'
    
    const { labelWidth: textLength } = this.generateField({ 
      label: 'Accounts Receivable', 
      value: totalAmount, 
      coordinates: [x, y + 5], 
      delimiter, 
      lineLength: 0,
    })

    this.generateField({ 
      label: 'Amount Paid', 
      value: paidAmount, 
      coordinates: [x, y], 
      delimiter, 
      textLength,
      lineLength: 0,
    })
    
    this.generateField({ 
      label: 'Customer', 
      value: name, 
      coordinates: [x, y - 5], 
      delimiter, 
      textLength,
      lineLength: 0,
    })

    this.generateField({ 
      label: 'Balance', 
      value: balance, 
      coordinates: [x, y + 10], 
      textLength, 
      delimiter, 
      lineLength: 0,
    })    
  }

  /**
   * Description
   * @param {{ totalAmount: number; }} params
   * @param {SummaryItem} body
   * @returns {any}
   */
  __table ({ totalAmount = '', paidAmount = '', balance = '' }, body) {
    this.doc.autoTable({
      head: [['Receipt No', 'Items', 'Amount', 'Paid', 'Unpaid']],
      startY: 60,
      columnStyles: {
        amount: { halign: 'right' },
        paidAmount: { halign: 'right' },
        unpaidAmount: { halign: 'right' },
        noOfItems: { halign: 'center' },
      },
      body: [
        ...body,
        [
          { content: 'Total', colSpan: 2, styles: { halign: 'left', fontStyle: 'bold' } }, 
          { content: totalAmount, styles: { halign: 'right' } },
          { content: paidAmount, styles: { halign: 'right' } },
          { content: balance, styles: { halign: 'right' } },
        ],
      ],
      columns: [
        { header: 'Receipt No', dataKey: 'receiptNo' },
        { header: 'Items', dataKey: 'noOfItems' },
        { header: 'Price', dataKey: 'amount', styles: { halign: 'right' } },
        { header: 'Disc', dataKey: 'paidAmount', styles: { halign: 'right' } },
        { header: 'Amount', dataKey: 'unpaidAmount', styles: { halign: 'right' } },
      ],
    })
  }

  /**
   * Description
   * @param {string} receiptNo
   * @param {boolean} [cancel]=false
   * @returns {void}
   */
  __save (customerName, cancel = false) {
    const name = !cancel ? `${this.getStoreName()}_summary_of_loans_${customerName}_${new Date().getTime()}` : `cancelled_${this.getStoreName()}_summary_of_loans_${customerName}_${new Date().getTime()}`
    this.doc.save(name)
  }

  /**
   * Description
   * @param {SummaryData} data
   * @param {SummaryItem[]} items
   * @param {boolean} [cancel]
   * @returns {any}
   */
  generate (data, items, cancel) {
    this.__header(cancel)
    this.__subHeader(data)
    this.__table(data, items)
    this.__save(data.name, cancel)
  }
}

/**
 * @typedef SummaryData
 * @property {string} name
 * @property {string} paidAmount
 * @property {string} totalAmount
 * @property {string} balance
 * @property {string} address
 * 
 * @typedef SummaryItem
 * @property {string} receiptNo
 * @property {string} noOfItems
 * @property {string} amount
 * @property {string} paidAmount
 * @property {string} unpaidAmount
 */
